import React, { useEffect, useState, useCallback } from "react";
import { Modal, Button, Spinner, Table, Card, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import blogFetch from "../../axios/config";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

interface Peca {
    ordem: string;
    chaveModApont: string;
    proximoCenTrab: string;
    lote: string;
    material: string;
    descricao: string;
    tipo: string;
}

interface Componente {
    desenho_Key?: number;
    lote?: string;
    pacitem_key?: number;
    loteRefugo?: string;
    veioDoBanco?: boolean; // Essa flag será calculada ao carregar
    desenho_OrdemSerial?: string;
    desenho_Operation?: string;
    desenho_ClaveModelo?: string;
    adicionaL_1?: string;
    adicionaL_2?: string;
    adicionaL_3?: string;
    adicionaL_5?: string;
    desenho_CodMat?: string;
    desenho_Descricao?: string;
    desenho_ZTipo?: boolean;
    loteInvalido?: boolean;
}

const EMPTY_PECA: Peca = {
    ordem: "", chaveModApont: "", proximoCenTrab: "",
    lote: "", material: "", descricao: "", tipo: "",
};

interface Props {
    ordemInicial?: string;
}

export const TabelaResumoOrdens = ({ ordemInicial }: Props): JSX.Element => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [peca, setPeca] = useState<Peca>(EMPTY_PECA);
    const [componentes, setComponentes] = useState<Componente[]>([]);
    const [loadingComp, setLoadingComp] = useState<boolean>(false);
    const [matricula, setMatricula] = useState<string>("");
    const [SelectMotivoRemocao, setSelectMotivoRemocao] = useState<string>("");

    const [modalLimparLote, setModalLimparLote] = useState<{ show: boolean; index: number | null }>({
        show: false,
        index: null
    });
    const [checkboxRemocao, setCheckboxRemocao] = useState(false);

    const user = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        const matriculaStorage = localStorage.getItem("listaMontagem_matriculas_SAP");
        if (matriculaStorage) {
            try {
                const matriculaObj = JSON.parse(matriculaStorage);
                setMatricula(matriculaObj.principal || matriculaObj.secundaria || "");
            } catch (e) {
                setMatricula(matriculaStorage);
            }
        } else if (user.user_SAPMatricula) {
            setMatricula(user.user_SAPMatricula);
        } else {
            console.warn("Matrícula não encontrada no localStorage nem no context");
        }
    }, [user]);

    const resetForm = useCallback(() => {
        setPeca(EMPTY_PECA);
        setComponentes([]);
        setLoadingComp(false);
    }, []);

    const handleLoteChange = async (idx: number, value: string) => {
        setComponentes((prev) =>
            prev.map((item, i) =>
                i === idx ? { ...item, lote: value } : item
            )
        );

        try {
            const comp = componentes[idx];
            if (!comp) return;

            if (value.trim().length >= 10) {
                const { data } = await blogFetch.get(`IAcompanhamentoMontagem/estoque/${encodeURIComponent(value)}`);

                setComponentes((prev) =>
                    prev.map((item, i) =>
                        i === idx ? { ...item, loteInvalido: true, veioDoBanco: data } : item
                    )
                );

                if (!data) toast.error(`Não existe estoque para o lote ${value}`);
            }
        } catch (err) {
            console.error('Erro ao verificar estoque do lote:', err);
        }
    };

    const abrirModalLimpar = (idx: number) => {
        setCheckboxRemocao(false);
        setModalLimparLote({ show: true, index: idx });
    };

    const confirmarLimpezaLote = async () => {
        if (modalLimparLote.index !== null) {
            const componenteAlvo = componentes[modalLimparLote.index];

            try {
                // Payload para enviar ao back-end
                const dadosRemocao = {
                    ordem: peca.ordem,
                    material: componenteAlvo.desenho_CodMat,
                    lote: componenteAlvo.lote,
                    CorteManual: checkboxRemocao,
                    matricula: matricula,
                    motivoRemocao: SelectMotivoRemocao || "NÃO INFORMADO",
                    pacote_key: componenteAlvo.pacitem_key || null
                };

                await blogFetch.post("IAcompanhamentoMontagem/Refugo", dadosRemocao);

                toast.success("Notificação de remoção enviada ao servidor.");
                setSelectMotivoRemocao("");
                setCheckboxRemocao(false);

            } catch (error) {
                console.error("Erro ao enviar remoção para o back-end:", error);
                toast.error("Erro ao comunicar remoção ao servidor, mas o lote será limpo localmente.");
                setSelectMotivoRemocao("");
                setCheckboxRemocao(false);
            }


            // LIMPEZA LOCAL (Acontece independente do checkbox, ou após o envio)
            setComponentes((prev) =>
                prev.map((item, i) =>
                    i === modalLimparLote.index
                        ? {
                            ...item,
                            lote: "",
                            loteRefugo: undefined,
                            loteInvalido: false,
                            veioDoBanco: false // Destrava o input
                        }
                        : item
                )
            );

            if (!checkboxRemocao) {
                toast.success("Lote removido da tela com sucesso!");
            }
        }

        // Fecha o modal e reseta estados
        setModalLimparLote({ show: false, index: null });
        setCheckboxRemocao(false);
    };

    const cancelarLimpezaLote = () => {
        setModalLimparLote({ show: false, index: null });
    };

    useEffect(() => {
        if (ordemInicial) {
            buscarDadosOrdem(ordemInicial);
        }
    }, [ordemInicial]);

    const buscarDadosOrdem = async (ordem: string) => {
        try {
            const { data: pecaData } = await blogFetch.get(`IAcompanhamentoMontagem/peca/${ordem}`);

            if (!pecaData) {
                toast.error("Peça não encontrada.");
                return;
            }

            setPeca({
                ordem: pecaData.ordem_Serial,
                chaveModApont: pecaData.ordem_ClaveAtual,
                proximoCenTrab: pecaData.ordem_SetAtual,
                tipo: `${pecaData.ordem_Part_Cod} - ${pecaData.ordem_Part_Short}`,
                lote: pecaData.pedido_Codigo,
                material: pecaData.ordem_CodMaterial,
                descricao: pecaData.ordem_VehDesc,
            });

            setIsModalOpen(false);
            setLoadingComp(true);

            const { data: compData } = await blogFetch.get(`IAcompanhamentoMontagem/componentes/${ordem}`);
            const lista = Array.isArray(compData) ? compData : compData?.rows || [];

            // AQUI ESTÁ O TRUQUE: Calculamos 'veioDoBanco' aqui
            setComponentes(lista.map((it: any) => ({
                ...it,
                loteInvalido: false,
                // Se tem lote preenchido na carga inicial, veio do banco e deve travar
                veioDoBanco: (it.lote && it.lote.trim().length > 0) ? true : false
            })));

        } catch (error: any) {
            toast.error(error.response?.data || "Erro na comunicação com o servidor.");
            resetForm();
        } finally {
            setLoadingComp(false);
        }
    };

    const handleApontar = async () => {
        if (!matricula) {
            toast.error("Matrícula não encontrada. Por favor, faça login novamente.");
            return;
        }

        try {
            const componentesComRefugo = componentes.map(c => ({
                ...c,
                loteRefugo: c.loteRefugo || null,
            }));

            const dadosApontamento = {
                peca,
                componentes: componentesComRefugo,
                matricula: matricula,
            };

            await blogFetch.post("IAcompanhamentoMontagem/apontar", dadosApontamento);
            toast.success("Apontamento realizado com sucesso!");

            await blogFetch.post("IAcompanhamentoMontagem/acompanhamento", {
                Ordem: peca.ordem,
                matricula: matricula,
            });

            resetForm();
        } catch (err) {
            console.error("Erro ao realizar apontamento:", err);
            toast.error("Erro ao realizar apontamento.");
        }
    };

    const existeLoteInvalido = componentes.some(c => !c.lote || c.lote.trim().length < 9);

    useEffect(() => {
        if (!peca.ordem.trim()) return;
        const debounce = setTimeout(() => buscarDadosOrdem(peca.ordem), 1350);
        return () => clearTimeout(debounce);
    }, [peca.ordem]);

    return (
        <div className="container-fluid p-0">
            <Card className="shadow-sm mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center bg-white py-3">
                    <h5 className="mb-0 fw-bold text-secondary">Resumo de Ordens</h5>
                    <div className="d-flex gap-2 align-items-center">
                        {matricula && (
                            <span className="badge bg-primary">Matrícula: {matricula}</span>
                        )}
                        <Button variant="primary" size="sm" onClick={() => { resetForm(); setIsModalOpen(true); }}>
                            <i className="bi bi-plus-lg me-1"></i> Adicionar Ordem
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive" style={{ maxHeight: "300px" }}>
                        <Table hover striped bordered size="sm" className="mb-0 text-center align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th>Ordem</th>
                                    <th>Chv. Mod. Apont</th>
                                    <th>Próximo Cen. Trab</th>
                                    <th>Tipo Peça</th>
                                    <th>Lote</th>
                                    <th>Material</th>
                                    <th>Descrição</th>
                                </tr>
                            </thead>
                            <tbody>
                                {peca.ordem ? (
                                    <tr>
                                        <td>{peca.ordem}</td>
                                        <td>{peca.chaveModApont}</td>
                                        <td>{peca.proximoCenTrab}</td>
                                        <td>{peca.tipo}</td>
                                        <td>{peca.lote}</td>
                                        <td>{peca.material}</td>
                                        <td>{peca.descricao}</td>
                                    </tr>
                                ) : (
                                    <tr><td colSpan={7} className="text-muted py-4">Nenhuma ordem selecionada</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm">
                <Card.Header className="bg-white py-3">
                    <h5 className="mb-0 fw-bold text-secondary">Consumo de Componentes</h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <div className="table-responsive" style={{ maxHeight: "400px" }}>
                        <Table hover striped bordered size="sm" className="mb-0 align-middle">
                            <thead className="table-light text-center">
                                <tr>
                                    <th>#</th>
                                    <th>Ordem</th>
                                    <th>Operação</th>
                                    <th>Chv. Mod. Apont</th>
                                    <th>Posição</th>
                                    <th>Componente</th>
                                    <th>Descrição</th>
                                    <th>Qtd. UM</th>
                                    <th>UM</th>
                                    <th>Depósito</th>
                                    <th style={{ minWidth: "320px" }}>Lote</th>
                                    <th>Z_Tipo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingComp ? (
                                    <tr>
                                        <td colSpan={12} className="text-center py-5">
                                            <Spinner animation="border" variant="primary" />
                                        </td>
                                    </tr>
                                ) : componentes.length > 0 ? (
                                    componentes.map((c, index) => (
                                        <tr key={c.desenho_Key ?? index} className={c.loteInvalido ? 'table-danger' : ''}>
                                            <td className="text-center">{index + 1}</td>
                                            <td>{c.desenho_OrdemSerial}</td>
                                            <td className="text-center">{c.desenho_Operation}</td>
                                            <td>{c.desenho_ClaveModelo}</td>
                                            <td>{c.adicionaL_1}</td>
                                            <td className="fw-bold">{c.desenho_CodMat?.replace(/^0+/, '')}</td>
                                            <td className="small">{c.desenho_Descricao}</td>
                                            <td className="text-center">{c.adicionaL_2?.trim()}</td>
                                            <td className="text-center">{c.adicionaL_3}</td>
                                            <td className="text-center">{c.adicionaL_5}</td>
                                            <td>
                                                <div className="d-flex gap-1 align-items-center">
                                                    <input
                                                        type="text"
                                                        className={`form-control form-control-sm ${c.loteInvalido === false ? '' : 'border-danger'}`}
                                                        value={c.lote ?? ""}
                                                        /* AQUI: Se veio do banco é TRUE, então disabled é TRUE */
                                                        disabled={c.veioDoBanco === true || !!c.loteRefugo}
                                                        onChange={(e) => handleLoteChange(index, e.target.value)}
                                                        placeholder="Insira o lote"
                                                    />
                                                    {/* Mostra o botão de lixeira se tiver lote (mesmo travado) para poder limpar */}
                                                    {c.lote && c.lote.trim().length > 0 && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            type="button"
                                                            onClick={() => abrirModalLimpar(index)}
                                                            title="Remover lote"
                                                        >
                                                            <DeleteForeverIcon fontSize="small" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-center">{c.desenho_ZTipo}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={12} className="text-center text-muted py-4">Aguardando dados...</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-end mt-4">
                <Button
                    variant="success"
                    className="px-5 fw-bold shadow-sm"
                    onClick={handleApontar}
                    disabled={existeLoteInvalido || componentes.length === 0 || !matricula}
                >
                    <i className="bi bi-check-circle me-2"></i>
                    Confirmar Apontamento
                </Button>
            </div>

            <Modal show={isModalOpen} onHide={() => setIsModalOpen(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="h6 fw-bold">Adicionar Nova Ordem</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4">
                    <div className="form-group">
                        <label className="form-label fw-semibold text-muted small">NÚMERO DA ORDEM</label>
                        <input
                            type="text"
                            className="form-control form-control-lg text-primary fw-bold"
                            placeholder="Ex: 123456"
                            value={peca.ordem}
                            autoFocus
                            onChange={(e) => setPeca(prev => ({ ...prev, ordem: e.target.value }))}
                        />
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={modalLimparLote.show} onHide={cancelarLimpezaLote} centered backdrop="static">
                <Modal.Header closeButton className="bg-danger bg-opacity-10 border-danger">
                    <Modal.Title className="h6 fw-bold text-danger d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Confirmar Remoção
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4">
                    <div className="text-center">
                        <div className="mb-3">
                            <i className="bi bi-trash3 text-danger" style={{ fontSize: "3rem" }}></i>
                        </div>
                        <h6 className="fw-bold mb-2">Tem certeza que deseja remover este lote?</h6>
                        <p className="text-muted mb-0">
                            {modalLimparLote.index !== null && componentes[modalLimparLote.index] && (
                                <>
                                    <strong>Componente:</strong> {componentes[modalLimparLote.index].desenho_CodMat}
                                    <br />
                                    <strong>Lote atual:</strong> {componentes[modalLimparLote.index].lote}
                                </>
                            )}
                        </p>

                        <div className="mt-4 text-start bg-light p-3 rounded">
                            <Form.Check
                                type="checkbox"
                                id="checkbox-remover-opcao"
                                label="Corte Manual - Remover Lote"
                                checked={checkboxRemocao}
                                onChange={(e) => setCheckboxRemocao(e.target.checked)}
                                className="fw-semibold text-secondary"
                            />
                        </div>

                        <div className="mt-4 text-start bg-light p-3 rounded">
                            <Form.Select
                                id="select-motivo-remocao"
                                value={SelectMotivoRemocao}
                                onChange={(e) => setSelectMotivoRemocao(e.target.value)}
                                className="fw-semibold text-secondary"
                            >
                                <option selected value="">Selecione o motivo</option>
                                <option value="Z27_DESCARTE">Z27 DESCARTE MATERIAL MODULADO</option>
                                <option value="Z27_CAMADA">Z27 CAMADA FALTANTE</option>
                                <option value="Z25_ERRO">Z25 ERRO DE ARQUIVO</option>
                                <option value="Z23_MENOR">Z23 MATERIAL MENOR</option>
                                <option value="Z21_ABRASAO">Z21 ABRASÃO</option>
                                <option value="Z21_NAO_ENCONTRADO">Z21 MATERIAL NÃO ENCONTRADO</option>
                                <option value="Z21_MANCHADO">Z21 MATERIAL MANCHADO</option>
                                <option value="Z21_MARCA_COLA">Z21 MARCA DE COLA</option>
                                <option value="Z19_CONTAMINADO">Z19 MATERIAL CONTAMINADO</option>
                                <option value="Z19_MARCA_LINER">Z19 MARCA DE LINER</option>
                            </Form.Select>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={cancelarLimpezaLote} className="px-4">
                        <i className="bi bi-x-lg me-2"></i>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={confirmarLimpezaLote} className="px-4 fw-bold">
                        <i className="bi bi-trash3-fill me-2"></i>
                        Sim, Remover
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};